// Which shop am I working in?
//
// A person may belong to more than one organization — a publisher and their
// own imprint, an agency and the vendors it manages — and signing in cannot
// guess which one they came for. So it asks, as a dialog over the area, and
// remembers the answer in the url (`?org=`), which is what the rest of the
// area reads. One organization is never asked about.
import React from "react"
import Modal from "aldoo-ra/Modal"
import { orgLogoUrl } from "aldoo-ra/Organizations/api"
import { PlusIcon } from "@heroicons/react/24/outline"
import { Thumb } from "../AdminShared/ui"
import usePortalTexts from "./texts/usePortalTexts"
import { PORTAL } from "./texts/bundles"

export default function OrgPicker({
  orgs,
  onPick,
  onClose,
  // one more company: the application form (Vendor/index.jsx, ?apply=new)
  onNew = null,
  title = "Choose an organization",
}) {
  const t = usePortalTexts(PORTAL)
  return (
    // the whole screen on a phone (the admin_fullscreen variant); from 768px
    // a card in the middle of it
    <Modal
      isOpen
      onClose={onClose}
      variant="admin_fullscreen"
      title={t(title)}
      styleOverrides={{
        overlay: "fixed top-0 left-0 w-full h-full flex items-stretch md:items-center justify-center bg-black bg-opacity-70 z-50",
        container:
          "w-screen h-[100dvh] max-w-none bg-admin_paper border-0 rounded-none shadow-none overflow-hidden flex flex-col p-0 m-0 md:w-full md:h-auto md:max-h-[80vh] md:max-w-[560px] md:rounded-xl md:border md:border-admin_bordered md:shadow-lg",
        header: "relative flex justify-between items-center px-4 md:px-5 pt-5 pb-3 shrink-0",
        title: "flex-1 text-[24px] md:text-[26px] font-bold leading-tight text-admin_text",
      }}
    >
      <div className="flex flex-col h-full min-h-0" data-testid="org-picker">
        {/* one row per organization, a plain line between them */}
        <div className="flex-1 min-h-0 flex flex-col divide-y divide-admin_bordered overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 pb-[env(safe-area-inset-bottom)]">
          {orgs.map((org) => (
            <button
              key={org._id}
              type="button"
              onClick={() => onPick(org._id)}
              className="flex items-center gap-4 px-2 py-3.5 hover:bg-admin_neutral_1/50 text-left transition-colors"
              data-testid="org-picker-item"
            >
              <Thumb src={org.logo ? orgLogoUrl(org._id, org.logoUpdatedAt) : null} name={org.name} className="w-11 h-11 rounded-full text-base shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold truncate" data-testid="org-picker-name">{org.name}</span>
                <span className="block text-xs text-admin_text_secondary truncate">
                  {org.cid || t`no company id`}
                  {org.role && org.role !== "platform-admin" ? ` · ${t(org.role)}` : ""}
                  {org.productCount ? ` · ${t("%s products", org.productCount)}` : ""}
                </span>
              </span>
            </button>
          ))}
          {onNew && (
            <button
              type="button"
              onClick={onNew}
              className="flex items-center gap-4 px-2 py-3.5 hover:bg-admin_neutral_1/50 text-left text-admin_primary transition-colors"
              data-testid="org-picker-new"
            >
              <span className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center bg-admin_primary/10">
                <PlusIcon className="w-6 h-6" />
              </span>
              <span className="font-semibold">{t`New organization`}</span>
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
